import React, { useEffect, useMemo, useRef, useState } from 'react';
import { displayDateToIso, isoToDisplayDate, toIsoDate } from '../../utils/dateFormat';
import './DateInput.scss';

/**
 * A date field that always displays dd-mm-yyyy (platform standard, user 2026-09-08).
 *
 * A native date input cannot be used for this: browsers render it in the *user's* locale and
 * expose no way to override that, so the same field reads dd-mm-yyyy in India and mm-dd-yyyy in
 * the US. This renders a masked text input instead, backed by a hidden native picker so users
 * keep the calendar affordance.
 *
 * The public value is always ISO `yyyy-mm-dd` — identical to what the native date input emitted —
 * so form state, validation and request payloads are unaffected by the swap.
 */
interface DateInputProps {
  /** ISO `yyyy-mm-dd`, or '' when unset. */
  value?: string | null;
  /** Receives ISO `yyyy-mm-dd`, or '' when the field is cleared. */
  onChange?: (isoValue: string) => void;
  onBlur?: () => void;
  id?: string;
  name?: string;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  /** ISO `yyyy-mm-dd` bounds, forwarded to the native picker and enforced on commit. */
  min?: string;
  max?: string;
  placeholder?: string;
  'aria-label'?: string;
}

/**
 * Keeps only digits and re-inserts the dd-mm-yyyy separators as the user types.
 *
 * The separator appears as soon as a day or month is complete, so a typed hyphen is visible
 * straight away rather than only once the next digit arrives. Typing a separator after a
 * single digit pads it ("1-" becomes "01-"). While deleting, no trailing separator is
 * re-added, otherwise backspace could never get past it.
 */
const maskAsDisplayDate = (raw: string, deleting: boolean): string => {
  let digits = raw.replace(/\D/g, '');
  if (!deleting && /[-/.]$/.test(raw) && (digits.length === 1 || digits.length === 3)) {
    digits = `${digits.slice(0, -1)}0${digits.slice(-1)}`;
  }
  digits = digits.slice(0, 8);
  if (digits.length < 2) return digits;
  if (digits.length === 2) return deleting ? digits : `${digits}-`;
  if (digits.length < 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  if (digits.length === 4) {
    return deleting ? `${digits.slice(0, 2)}-${digits.slice(2)}` : `${digits.slice(0, 2)}-${digits.slice(2)}-`;
  }
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
};

/** Today (plus `offsetDays`) as ISO yyyy-mm-dd in the user's local calendar (not UTC). */
const localTodayIso = (offsetDays = 0): string => {
  const now = new Date();
  now.setDate(now.getDate() + offsetDays);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

export const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  onBlur,
  id,
  name,
  className = '',
  style,
  disabled = false,
  readOnly = false,
  required = false,
  min,
  max,
  placeholder = 'dd-mm-yyyy',
  'aria-label': ariaLabel
}) => {
  const displayValue = useMemo(() => isoToDisplayDate(value), [value]);
  const [text, setText] = useState(displayValue);
  const [focused, setFocused] = useState(false);
  // Why the last typed entry was thrown away; shown under the field until the next edit.
  const [rejection, setRejection] = useState('');
  const pickerRef = useRef<HTMLInputElement>(null);

  // Mirror external changes (async load, reset, another field deriving this one) — but never
  // while the user is mid-edit, or their partially typed date would be yanked away.
  useEffect(() => {
    if (!focused) setText(displayValue);
  }, [displayValue, focused]);

  const withinBounds = (iso: string): boolean => {
    if (min && iso < min) return false;
    if (max && iso > max) return false;
    return true;
  };

  const commit = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      onChange?.('');
      return;
    }
    const iso = displayDateToIso(trimmed);
    // Incomplete or impossible (31/02) dates are not committed; the blur handler below
    // restores the last good value so the field never holds an un-parseable string.
    if (iso && withinBounds(iso)) onChange?.(iso);
  };

  /** Tells the user why an entry was not accepted, instead of it silently vanishing. */
  const rejectionFor = (entered: string, iso: string): string => {
    if (!iso) return `${entered} is not a valid date. Enter it as dd-mm-yyyy.`;
    const today = localTodayIso();
    if (max && iso > max) {
      if (max === today) return `${entered} is not allowed: the date cannot be in the future.`;
      if (max === localTodayIso(-1)) return `${entered} is not allowed: the date cannot be today or in the future.`;
      return `${entered} is not allowed: the date cannot be after ${isoToDisplayDate(max)}.`;
    }
    if (min && iso < min) {
      if (min === today) return `${entered} is not allowed: the date cannot be in the past.`;
      if (min === localTodayIso(1)) return `${entered} is not allowed: the date cannot be today or in the past.`;
      return `${entered} is not allowed: the date cannot be before ${isoToDisplayDate(min)}.`;
    }
    return '';
  };

  const handleBlur = () => {
    setFocused(false);
    const entered = text.trim();
    const iso = displayDateToIso(entered);
    if (entered && (!iso || !withinBounds(iso))) {
      setRejection(rejectionFor(entered, iso));
      setText(displayValue); // revert to last committed value
    }
    onBlur?.();
  };

  const openPicker = () => {
    if (disabled || readOnly) return;
    const el = pickerRef.current;
    if (!el) return;
    // showPicker() is the only reliable way to open the calendar on a visually hidden input;
    // fall back to focus+click on browsers that lack it.
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
        return;
      } catch {
        /* Safari throws when the call is not user-activated — fall through. */
      }
    }
    el.focus();
    el.click();
  };

  return (
    // NOTE: do not name this `date-input` — Appointments.scss already defines that class
    // globally with a full form-control border, which would double-border every field.
    <>
    <div className={`facilon-date-field ${disabled || readOnly ? 'facilon-date-field--locked' : ''}`}>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        id={id}
        name={name}
        className={className}
        style={style}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        placeholder={placeholder}
        aria-label={ariaLabel}
        value={text}
        onFocus={() => setFocused(true)}
        onChange={(event) => {
          const raw = event.target.value;
          const masked = maskAsDisplayDate(raw, raw.length < text.length);
          setRejection('');
          setText(masked);
          commit(masked);
        }}
        onBlur={handleBlur}
      />
      {!disabled && !readOnly && (
        <button
          type="button"
          className="facilon-date-field__picker-btn"
          onClick={openPicker}
          tabIndex={-1}
          aria-label="Open calendar"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
          </svg>
        </button>
      )}
      {/* Hidden native picker: supplies the OS calendar. Its own rendering is never seen, so the
          fact that IT formats per locale does not matter. */}
      <input
        ref={pickerRef}
        type="date"
        className="facilon-date-field__native"
        tabIndex={-1}
        aria-hidden="true"
        min={min}
        max={max}
        // Must be strict yyyy-mm-dd or the browser rejects it and logs a warning.
        value={toIsoDate(value)}
        onChange={(event) => {
          setRejection('');
          setText(isoToDisplayDate(event.target.value));
          onChange?.(event.target.value);
        }}
      />
    </div>
    {/* Rendered directly before the form's own error line so DateInput.scss can hide that
        line while this one shows: one message per field, and this one is the specific one. */}
    {rejection && (
      <div className="invalid-feedback d-block facilon-date-field__rejection" role="alert">
        {rejection}
      </div>
    )}
    </>
  );
};

export default DateInput;
